import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { MapPin, Plus, Pencil, Trash2, Star, ExternalLink, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_LABELS = [
  ['mon', 'Monday'], ['tue', 'Tuesday'], ['wed', 'Wednesday'],
  ['thu', 'Thursday'], ['fri', 'Friday'],
];

const defaultHours = () => {
  const h = {};
  ['mon', 'tue', 'wed', 'thu', 'fri'].forEach((k) => {
    h[k] = { closed: false, open: '09:00', close: '16:00' };
  });
  h.sat = { closed: true, open: '09:00', close: '16:00' };
  h.sun = { closed: true, open: '09:00', close: '16:00' };
  return h;
};

const EMPTY = {
  name: '', line1: '', line2: '', city: '', state: '', postal: '',
  country: 'USA', active: true, is_default: false, sort_order: 0,
  hours: defaultHours(),
};

export default function AppointmentLocations() {
  const [rows, setRows] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // location_id being edited, or 'new'
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/owner-admin/appointments/locations`, { credentials: 'include' });
      if (!r.ok) throw new Error('load');
      const j = await r.json();
      setRows(j.locations || []);
    } catch { toast.error('Could not load office locations'); setRows([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing('new'); setForm({ ...EMPTY, hours: defaultHours() }); setDialogOpen(true); };
  const openEdit = (loc) => {
    setEditing(loc.location_id);
    setForm({
      name: loc.name || '', line1: loc.line1 || '', line2: loc.line2 || '',
      city: loc.city || '', state: loc.state || '', postal: loc.postal || '',
      country: loc.country || 'USA', active: !!loc.active,
      is_default: !!loc.is_default, sort_order: loc.sort_order || 0,
      hours: loc.hours || defaultHours(),
    });
    setDialogOpen(true);
  };

  const setDay = (key, patch) => setForm((f) => ({
    ...f,
    hours: { ...f.hours, [key]: { ...(f.hours?.[key] || {}), ...patch } },
  }));

  const valid =
    form.name.trim() && form.line1.trim() && form.city.trim() &&
    form.state.trim() && form.postal.trim();

  const save = async () => {
    if (!valid) { toast.error('Fill name, address line, city, state, and postal'); return; }
    setBusy(true);
    try {
      const url = editing === 'new'
        ? `${API_URL}/api/owner-admin/appointments/locations`
        : `${API_URL}/api/owner-admin/appointments/locations/${editing}`;
      const r = await fetch(url, {
        method: editing === 'new' ? 'POST' : 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const j = await r.json();
      if (!r.ok) { toast.error(j.detail || 'Save failed'); return; }
      toast.success(editing === 'new' ? 'Location added' : 'Location updated');
      setDialogOpen(false);
      await load();
    } catch { toast.error('Save failed'); }
    finally { setBusy(false); }
  };

  const remove = async (loc) => {
    if (!window.confirm(`Delete "${loc.name}"? This cannot be undone. Active bookings that reference this location keep their address, but it will no longer appear as an option.`)) return;
    try {
      const r = await fetch(
        `${API_URL}/api/owner-admin/appointments/locations/${loc.location_id}`,
        { method: 'DELETE', credentials: 'include' },
      );
      const j = await r.json();
      if (!r.ok) { toast.error(j.detail || 'Delete failed'); return; }
      toast.success('Location deleted');
      await load();
    } catch { toast.error('Delete failed'); }
  };

  const makeDefault = async (loc) => {
    setBusy(true);
    try {
      const r = await fetch(
        `${API_URL}/api/owner-admin/appointments/locations/${loc.location_id}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: loc.name, line1: loc.line1, line2: loc.line2 || '',
            city: loc.city, state: loc.state, postal: loc.postal,
            country: loc.country || 'USA', active: true,
            is_default: true, sort_order: loc.sort_order || 0,
            hours: loc.hours || null,
          }),
        },
      );
      if (!r.ok) { toast.error('Could not set default'); return; }
      toast.success(`${loc.name} is now the default`);
      await load();
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="admin-locations-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-orange-500" /> Office Locations
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Physical addresses clients can pick when booking an in-person meeting.
            The <span className="font-semibold">default</span> is used when no location is chosen.
          </p>
        </div>
        <Button size="sm" onClick={openNew} data-testid="admin-add-location-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add location
        </Button>
      </CardHeader>
      <CardContent>
        {rows === null ? (
          <p className="text-sm text-muted-foreground">Loading locations…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No locations yet — add one to enable in-person bookings.</p>
        ) : (
          <div className="space-y-3">
            {rows.map((loc) => (
              <div key={loc.location_id}
                   className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-card"
                   data-testid={`location-row-${loc.location_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="font-semibold">{loc.name}</span>
                    {loc.is_default && (
                      <Badge className="bg-orange-500/15 text-orange-600 border-orange-500/30 gap-1">
                        <Star className="w-3 h-3" /> Default
                      </Badge>
                    )}
                    {!loc.active && (
                      <Badge className="bg-slate-500/15 text-slate-500 border-slate-500/30">Inactive</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {loc.one_line || `${loc.line1}, ${loc.city}, ${loc.state} ${loc.postal}`}
                  </p>
                  {loc.map_url && (
                    <a href={loc.map_url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-emerald-600 hover:underline">
                      Google Maps <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {!loc.is_default && loc.active && (
                    <Button size="sm" variant="ghost"
                            onClick={() => makeDefault(loc)}
                            disabled={busy}
                            data-testid={`location-make-default-${loc.location_id}`}>
                      <Star className="w-4 h-4" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => openEdit(loc)}
                          data-testid={`location-edit-${loc.location_id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost"
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          onClick={() => remove(loc)}
                          data-testid={`location-delete-${loc.location_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="location-dialog">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Add office location' : 'Edit office location'}</DialogTitle>
            <DialogDescription>
              Address and weekly hours for this office. Only active locations appear on the public booking page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Nickname *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Upper Sandusky HQ" className="mt-1"
                data-testid="location-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Address line *</label>
              <Input value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })}
                placeholder="108 South Sandusky Avenue" className="mt-1"
                data-testid="location-line1" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Suite / Unit (optional)</label>
              <Input value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })}
                placeholder="Suite 200" className="mt-1"
                data-testid="location-line2" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">City *</label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="Upper Sandusky" className="mt-1"
                  data-testid="location-city" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">State *</label>
                <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                  placeholder="OH" className="mt-1"
                  data-testid="location-state" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">ZIP *</label>
                <Input value={form.postal} onChange={(e) => setForm({ ...form, postal: e.target.value })}
                  placeholder="43351" className="mt-1"
                  data-testid="location-postal" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-semibold">Active</p>
                <p className="text-xs text-muted-foreground">Only active locations appear on /book.</p>
              </div>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })}
                data-testid="location-active-toggle" />
            </div>
            <div className="rounded-lg border p-3 space-y-2" data-testid="location-hours-editor">
              <div>
                <p className="text-sm font-semibold flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-orange-500" /> Weekly hours (Eastern Time)
                </p>
                <p className="text-xs text-muted-foreground">
                  In-person booking slots at this office only show inside these hours.
                  Weekends are unavailable platform-wide. Hours are capped to the 9:00 AM – 4:00 PM booking window.
                </p>
              </div>
              {DAY_LABELS.map(([key, label]) => {
                const d = form.hours?.[key] || { closed: false, open: '09:00', close: '16:00' };
                return (
                  <div key={key} className="flex items-center gap-2" data-testid={`hours-row-${key}`}>
                    <span className="text-xs font-medium w-20 shrink-0">{label}</span>
                    <Switch
                      checked={!d.closed}
                      onCheckedChange={(v) => setDay(key, { closed: !v })}
                      data-testid={`hours-open-toggle-${key}`}
                    />
                    {d.closed ? (
                      <span className="text-xs text-muted-foreground italic">Closed</span>
                    ) : (
                      <>
                        <Input
                          type="time"
                          value={d.open || '09:00'}
                          onChange={(e) => setDay(key, { open: e.target.value })}
                          className="h-8 w-28 text-xs"
                          data-testid={`hours-open-${key}`}
                        />
                        <span className="text-xs text-muted-foreground">to</span>
                        <Input
                          type="time"
                          value={d.close || '16:00'}
                          onChange={(e) => setDay(key, { close: e.target.value })}
                          className="h-8 w-28 text-xs"
                          data-testid={`hours-close-${key}`}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-semibold">Set as default</p>
                <p className="text-xs text-muted-foreground">Used when a client doesn&apos;t pick a specific location.</p>
              </div>
              <Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: !!v })}
                data-testid="location-default-toggle" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={!valid || busy} data-testid="location-save-btn">
              {busy ? 'Saving…' : (editing === 'new' ? 'Add location' : 'Save changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
